import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { LogOut, Mail, RefreshCw, Trash2, CheckCircle2, Archive, Inbox } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "lt_admin_token";

function LoginView({ onAuth }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Connecté");
      onAuth(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--lt-night)] grain flex items-center justify-center p-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white rounded-[2px] p-8 border border-zinc-200"
      >
        <div className="flex items-center gap-3 mb-6">
          <Logo size={40} variant="light" />
          <div>
            <div className="font-display font-extrabold text-lg tracking-tight">
              LA TECHNOLOGIE
            </div>
            <div className="overline text-zinc-500">Espace admin</div>
          </div>
        </div>
        <h1 className="font-display text-2xl font-bold mb-2">Connexion</h1>
        <p className="text-sm text-zinc-600 mb-6">
          Accès réservé à l'équipe interne de La Technologie SARL.
        </p>
        <label className="overline text-zinc-600 mb-2 block">
          Mot de passe administrateur
        </label>
        <input
          type="password"
          required
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="lt-input"
          placeholder="••••••••"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="beam-btn w-full mt-5 bg-[var(--lt-orange)] hover:bg-[var(--lt-orange-deep)] text-white py-3 font-semibold rounded-[2px] disabled:opacity-60"
        >
          {loading ? "Connexion…" : "Se connecter"}
        </button>
        <a
          href="/"
          className="block text-center text-xs text-zinc-500 mt-5 hover:text-zinc-800"
        >
          ← Retour au site
        </a>
      </form>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    new: { label: "Nouveau", c: "bg-[#D4AF37] text-[#0A1326]" },
    read: { label: "Lu", c: "bg-[#142042] text-[#F5F5F5]/80 border border-[#D4AF37]/20" },
    archived: { label: "Archivé", c: "bg-[#050a18] text-[#D4AF37] border border-[#D4AF37]/30" },
  }[status] || { label: status, c: "bg-zinc-100 text-zinc-700" };
  return (
    <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-[2px] ${cfg.c}`}>
      {cfg.label}
    </span>
  );
}

function Dashboard({ token, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/messages`, auth);
      setMessages(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
        return;
      }
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/messages/${id}`, { status }, auth);
      setMessages((ms) => ms.map((m) => (m.id === id ? data : m)));
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Échec de la mise à jour");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, auth);
      setMessages((ms) => ms.filter((m) => m.id !== id));
      toast.success("Message supprimé");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const filtered = filter === "all" ? messages : messages.filter((m) => m.status === filter);
  const counts = {
    all: messages.length,
    new: messages.filter((m) => m.status === "new").length,
    read: messages.filter((m) => m.status === "read").length,
    archived: messages.filter((m) => m.status === "archived").length,
  };

  return (
    <div className="min-h-screen bg-[#0A1326]" data-testid="admin-dashboard">
      <header className="bg-[#050a18]/90 backdrop-blur-md border-b border-[#D4AF37]/15 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={36} variant="light" />
            <div className="leading-none">
              <div className="font-display font-extrabold tracking-[0.06em] text-sm text-[#F5F5F5]">
                HAKKILO — ADMIN
              </div>
              <div className="font-display text-[9px] tracking-[0.32em] text-[#D4AF37] mt-1 font-semibold">
                MESSAGES DE CONTACT
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[#D4AF37]/25 text-[#F5F5F5] text-sm rounded-[2px] hover:bg-[#142042]"
            >
              <RefreshCw size={14} /> Rafraîchir
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[#D4AF37]/25 text-[#F5F5F5] text-sm rounded-[2px] hover:bg-[#142042]"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {[
            { id: "all", label: "Tous", icon: Inbox },
            { id: "new", label: "Nouveaux", icon: Mail },
            { id: "read", label: "Lus", icon: CheckCircle2 },
            { id: "archived", label: "Archivés", icon: Archive },
          ].map((f) => {
            const Ic = f.icon;
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`admin-filter-${f.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-[2px] border transition-colors ${
                  active
                    ? "bg-[#D4AF37] text-[#0A1326] border-[#D4AF37]"
                    : "bg-[#142042]/60 text-[#F5F5F5]/75 border-[#D4AF37]/20 hover:border-[#D4AF37]/50"
                }`}
              >
                <Ic size={14} />
                {f.label}
                <span className={`ml-1 text-[11px] ${active ? "text-[#0A1326]/70" : "text-[#F5F5F5]/40"}`}>
                  {counts[f.id]}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-20 text-[#F5F5F5]/55">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#F5F5F5]/55 border border-dashed border-[#D4AF37]/25 rounded-[2px] bg-[#142042]/40">
            Aucun message dans cette catégorie.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map((m) => (
              <article
                key={m.id}
                data-testid={`message-card-${m.id}`}
                className="bg-[#142042]/60 border border-[#D4AF37]/20 rounded-[2px] p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={m.status} />
                      <span className="text-[11px] text-[#F5F5F5]/45">
                        {new Date(m.created_at).toLocaleString("fr-FR", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </span>
                    </div>
                    <h3 className="font-display text-lg font-bold mt-3 truncate text-[#F5F5F5]">
                      {m.name}
                      {m.company ? <span className="text-[#F5F5F5]/55 font-normal"> · {m.company}</span> : null}
                    </h3>
                    <div className="text-sm text-[#F5F5F5]/65 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <a href={`mailto:${m.email}`} className="hover:text-[#D4AF37]">{m.email}</a>
                      {m.phone ? <span>{m.phone}</span> : null}
                    </div>
                    {m.service ? (
                      <div className="mt-2 text-[12px] text-[#D4AF37] font-semibold uppercase tracking-wider">
                        {m.service}
                      </div>
                    ) : null}
                  </div>
                </div>
                <p className="mt-4 text-[14px] text-[#F5F5F5]/85 leading-relaxed whitespace-pre-wrap">
                  {m.message}
                </p>
                <div className="mt-5 pt-4 border-t border-[#D4AF37]/15 flex flex-wrap items-center gap-2">
                  {m.status !== "read" && (
                    <button
                      onClick={() => updateStatus(m.id, "read")}
                      data-testid={`mark-read-${m.id}`}
                      className="text-xs px-3 py-1.5 border border-[#D4AF37]/25 text-[#F5F5F5]/80 rounded-[2px] hover:bg-[#D4AF37] hover:text-[#0A1326] transition-colors"
                    >
                      Marquer comme lu
                    </button>
                  )}
                  {m.status !== "archived" && (
                    <button
                      onClick={() => updateStatus(m.id, "archived")}
                      data-testid={`archive-${m.id}`}
                      className="text-xs px-3 py-1.5 border border-[#D4AF37]/25 text-[#F5F5F5]/80 rounded-[2px] hover:bg-[#D4AF37] hover:text-[#0A1326] transition-colors"
                    >
                      Archiver
                    </button>
                  )}
                  {m.status !== "new" && (
                    <button
                      onClick={() => updateStatus(m.id, "new")}
                      className="text-xs px-3 py-1.5 border border-[#D4AF37]/25 text-[#F5F5F5]/80 rounded-[2px] hover:bg-[#D4AF37] hover:text-[#0A1326] transition-colors"
                    >
                      Remettre en nouveau
                    </button>
                  )}
                  <button
                    onClick={() => remove(m.id)}
                    data-testid={`delete-${m.id}`}
                    className="text-xs px-3 py-1.5 border border-red-500/40 text-red-300 rounded-[2px] hover:bg-red-500/10 ml-auto inline-flex items-center gap-1"
                  >
                    <Trash2 size={12} /> Supprimer
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginView onAuth={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
